import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

import { getPlaylistEpisodes, getPlaylist } from '@common/redux/modules/VODPApp';
import { fetchPlaylistById, fetchPlaylistEpisodes } from '@common/redux/modules/VODPApp/reducers';
import { Icon, SmartLink } from '@common/ui/components';
import styled from 'styled-components';

import { buildPlaylistEpisodePath } from '@routers/routes';
import { EpisodeItem } from '../../PlaylistDetail/molcules';

type BottomSheetProps = {
  offsetBottom: number;
  channelCode: string;
  channelId: number;
};

function BottomSheet({ offsetBottom, channelCode, channelId }: BottomSheetProps) {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const { playlistId, postId } = useParams<{ playlistId: string; postId: string }>();

  const [isBottomSheetOpen, setBottomSheetOpen] = useState<boolean>(false);
  const [isBottomSheetFullOpen, setBottomSheetFullOpen] = useState<boolean>(false);

  const episodes = useSelector(getPlaylistEpisodes);
  const playlist = useSelector(getPlaylist);

  useEffect(() => {
    if (!playlistId || !channelId) return;

    dispatch(fetchPlaylistById({ seriesId: playlistId, channelId }));
    dispatch(fetchPlaylistEpisodes({ seriesId: playlistId, channelId }));
  }, [dispatch, playlistId, channelId]);

  const handleBottomSheet = () => {
    if (isBottomSheetFullOpen || isBottomSheetOpen) {
      setBottomSheetOpen(false);
      setBottomSheetFullOpen(false);
      return;
    }

    setBottomSheetOpen(true);
  };

  const handleSwipe = useSwipeable({
    onSwipedUp: () => {
      if (!isBottomSheetOpen) {
        setBottomSheetOpen(true);
        return;
      }
      setBottomSheetFullOpen(true);
    },
    onSwipedDown: () => {
      setBottomSheetOpen(false);
      setBottomSheetFullOpen(false);
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    trackTouch: true
  });

  return (
    <S_BottomSheet
      isBottomSheetOpen={isBottomSheetOpen}
      bottom={offsetBottom}
      isOpenFull={isBottomSheetFullOpen}
    >
      <S_Header {...handleSwipe}>
        <S_PlayListIcon iconName="ic_VideoPlaylist" size={16} fill />

        <S_PlaylistInfo>
          {playlist?.title}
          <S_Count>
            {t('str_54', {
              playlistVodCnt: playlist?.totalEpisodes,
              defaultValue: '총 {{playlistVodCnt}}개'
            })}
          </S_Count>
        </S_PlaylistInfo>

        <S_ArrowButton
          iconName={isBottomSheetOpen ? 'ic_ArrowDown' : 'ic_ArrowUp'}
          colorCode="grey500"
          size={16}
          onClick={handleBottomSheet}
        />

        {isBottomSheetOpen && <S_GreyBar />}
      </S_Header>

      {isBottomSheetOpen && (
        <S_Playlist>
          {episodes.length > 0 &&
            episodes.map(({ id, post }) => (
              <SmartLink
                key={id}
                to={buildPlaylistEpisodePath(channelCode, playlistId, post.id)}
                openNewTab={false}
              >
                <EpisodeItem
                  thumbnailSrc={post.vodResource.thumbnailSrc}
                  createdAt={post.insertedAt}
                  title={post.title}
                  runningTime={post.vodResource.duration}
                  isCurrentVOD={Number(postId) === post.id}
                />
              </SmartLink>
            ))}
          <S_EmptyDiv />
        </S_Playlist>
      )}
    </S_BottomSheet>
  );
}

const S_BottomSheet = styled.div<{
  isBottomSheetOpen: boolean;
  bottom: number;
  isOpenFull: boolean;
}>`
  background-color: white;
  border-radius: 24px 24px 0 0;
  bottom: 0;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
  height: ${({ isBottomSheetOpen, bottom, isOpenFull }) => {
    if (isBottomSheetOpen) {
      if (isOpenFull) return '100%';
      return `calc(100% - ${bottom}px)`;
    }

    return '72px';
  }};
  position: fixed;
  touch-action: 'pan-y';
  transition: height 0.2s ease-in-out;
  width: 100vw;
  z-index: 1;
`;

const S_Header = styled.div`
  display: flex;
  margin-bottom: 12px;
  padding-top: 20px;
  touch-action: 'pan-y';
`;

const S_Playlist = styled.div`
  display: 'flex';
  flex-direction: 'column-reverse';
  height: 100%;
  overflow: auto;
  touch-action: 'pan-y';
  &::-webkit-scrollbar {
    display: none !important;
  }
`;

const S_PlayListIcon = styled(Icon)`
  margin-left: 24px;
`;

const S_PlaylistInfo = styled.div`
  color: ${({ theme }) => theme.grey900};
  display: flex;
  flex-direction: column;
  font-size: ${({ theme }) => theme.fontSizeCaption1};
  gap: 4px;
  margin-left: 12px;
  text-align: left;
`;

const S_Count = styled.span`
  color: ${({ theme }) => theme.grey500};
`;

const S_ArrowButton = styled(Icon)`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-right: 24px;
  position: absolute;
  right: 0;
  top: 28px;
`;

const S_GreyBar = styled.div`
  background-color: #d8d8d8;
  border-radius: 2px;
  height: 4px;
  left: 0;
  margin: 0 131px 4px 59px;
  margin: auto;
  margin-top: 8px;
  position: absolute;
  right: 0;
  top: 0;
  width: 35px;
`;

const S_EmptyDiv = styled.div`
  height: 72px;
`;

export default BottomSheet;
